import * as THREE from 'three/webgpu';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import type { ImportedModelRaycastResolver } from '../../imported-model/selection/ImportedModelRaycastResolver';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { AssemblyEntitySelectionHandler } from './AssemblyEntitySelectionHandler';

//------ 装配体模式下基准点线面 开始------
/**
 * 判断对象是否可作为装配体模式下基准面参考实体。
 *
 * @param object 场景对象。
 * @returns 是否允许作为装配体基准面引用。
 */
function isAssemblyReferencePlaneSelectableObject(object: THREE.Object3D): boolean {
    return isFeatureFace(object)
        || isFeatureLine(object)
        || isFeaturePoint(object)
        || isDatumAxis(object)
        || isDatumPoint(object)
        || isDatumPlaneObject(object);
}

//------ 基准面选择调试追踪 开始------
/**
 * 装配体基准面选择调试日志开关。
 *
 * 说明：保留原基准面代理排查能力，后续稳定后可关闭。
 */
const ASSEMBLY_REFERENCE_PLANE_SELECTION_DEBUG_ENABLED = true;

/**
 * 输出装配体基准面选择处理器调试日志。
 *
 * @param message 日志说明。
 * @param payload 需要附带观察的运行时数据。
 */
function logAssemblyReferencePlaneSelectionDebug(message: string, payload?: Record<string, unknown>): void {
    if (!ASSEMBLY_REFERENCE_PLANE_SELECTION_DEBUG_ENABLED) {
        return;
    }
    console.log(`[装配体基准面选择调试] ${message}`, payload ?? {});
}

/**
 * 输出装配体基准面选择处理器调试堆栈。
 *
 * @param message 堆栈说明。
 */
function traceAssemblyReferencePlaneSelectionDebug(message: string): void {
    if (!ASSEMBLY_REFERENCE_PLANE_SELECTION_DEBUG_ENABLED) {
        return;
    }
    console.trace(`[装配体基准面选择调试] ${message}`);
}
//------ 基准面选择调试追踪 结束------

/**
 * 装配体模式下基准面选择处理器。
 *
 * 职责：
 * 1. 仅在装配体基准面命令期间处理拾取与移入高亮；
 * 2. 保留 imported-model 二阶段代理拾取与 hover 代理复用；
 * 3. 与普通基准面命令分离，避免普通模式依赖装配体选择链。
 */
export class AssemblyReferencePlaneEntitySelectionHandler extends AssemblyEntitySelectionHandler {
    /**
     * 创建装配体模式下基准面选择处理器。
     *
     * @param entitySelectionManager 实体选择管理器。
     * @param importedModelRaycastResolver 导入模型射线解析器。
     */
    public constructor(
        entitySelectionManager: EntitySelectionManager,
        importedModelRaycastResolver: ImportedModelRaycastResolver,
    ) {
        super(entitySelectionManager, importedModelRaycastResolver, { assemblyModeEnabled: false });
    }

    /**
     * 处理鼠标按下。
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标移动。
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }
        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /** @inheritdoc 使用当前模式的候选规则消费最新悬停坐标。 */
    public override consumeHoverInput(clientX: number, clientY: number): void {
        const hoveredPick = this.pickEntityAt(
            clientX,
            clientY,
            this.collectAssemblyReferencePlaneSelectionTargets(),
        );
        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));
    }

    /**
     * 处理鼠标抬起。
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标点击。
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const rawPicked = this.resolveAssemblyReferencePlaneSelectionPick(payload.clientX, payload.clientY);
        logAssemblyReferencePlaneSelectionDebug('点击原始命中对象', {
            rawPickedObject: rawPicked?.object ?? null,
            rawPickedObjectUuid: rawPicked?.object?.uuid ?? null,
            rawPickedObjectName: rawPicked?.object?.name ?? null,
            rawPickedObjectType: rawPicked?.object?.type ?? null,
            rawPickedObjectUserData: rawPicked?.object?.userData ?? null,
        });
        const picked = this.resolveClickSelectionPick(rawPicked);
        logAssemblyReferencePlaneSelectionDebug('点击解析后命中对象', {
            pickedObject: picked?.object ?? null,
            pickedObjectUuid: picked?.object?.uuid ?? null,
            pickedObjectName: picked?.object?.name ?? null,
            pickedObjectType: picked?.object?.type ?? null,
            pickedObjectUserData: picked?.object?.userData ?? null,
        });
        if (!picked) {
            traceAssemblyReferencePlaneSelectionDebug('点击解析后无可用命中对象');
            return;
        }

        const selectable = isAssemblyReferencePlaneSelectableObject(picked.object);
        logAssemblyReferencePlaneSelectionDebug('装配体基准面引用可选性判断', {
            selectable,
            object: picked.object,
        });
        if (!selectable) {
            traceAssemblyReferencePlaneSelectionDebug('命中对象未通过装配体基准面引用可选性判断');
            return;
        }

        const viewer = this.entitySelectionManager.renderer;
        const accepted = viewer.referencePlaneController.addSelectionByObject(picked.object);
        logAssemblyReferencePlaneSelectionDebug('控制器接收选择结果', {
            accepted,
            object: picked.object,
            viewModel: viewer.referencePlaneController.getViewModel(),
        });
        traceAssemblyReferencePlaneSelectionDebug('点击选择链路追踪');
        if (!accepted) {
            const rejectionMessage = viewer.referencePlaneController.getViewModel().state.preview.error
                ?? '当前实体不支持作为基准面参考';
            viewer.drawingNoticeCallback?.(rejectionMessage, 'warning');
            return;
        }

        viewer.referencePlaneController.syncSelectionVisuals();
    }

    /**
     * 收集框选候选对象。
     * @returns 空候选列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return [];
    }

    /**
     * 解析当前点击应消费的装配体基准面引用命中。
     *
     * 设计说明：
     * 1. imported-model 点线面代理在装配体中会先经 hover 二阶段挂载；
     * 2. 点击时优先复用当前 hover 命中，才能保证控制器、选择中心和代理保留链使用同一个对象实例；
     * 3. 若当前 hover 不可作为装配体基准面引用，再回退到常规拾取。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @returns 本次点击应消费的命中结果。
     */
    private resolveAssemblyReferencePlaneSelectionPick(
        clientX: number,
        clientY: number,
    ): THREE.Intersection<THREE.Object3D> | null {
        const hoveredPick = this.entitySelectionManager.getHoveredPick();
        if (hoveredPick?.object && isAssemblyReferencePlaneSelectableObject(hoveredPick.object)) {
            return hoveredPick;
        }

        return this.pickEntityAt(
            clientX,
            clientY,
            this.collectAssemblyReferencePlaneSelectionTargets(),
        );
    }

    /**
     * 收集测量叠加层点击候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureClickSelectionTargets(): THREE.Object3D[] {
        return this.collectAssemblyReferencePlaneSelectionTargets();
    }

    /**
     * 收集测量叠加层移入候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectAssemblyReferencePlaneSelectionTargets();
    }

    /**
     * 收集装配体基准面模式的可拾取对象。
     * @returns 可参与拾取的对象列表。
     */
    private collectAssemblyReferencePlaneSelectionTargets(): THREE.Object3D[] {
        return this.collectDatumReferenceInteractionTargets(this.collectClickSelectionTargets()).filter((candidate) => (
            isAssemblyReferencePlaneSelectableObject(candidate)
            || this.importedModelRaycastResolver.isImportedModelObject(candidate)
        ));
    }
}
//------ 装配体模式下基准点线面 结束------
